import { CartUpdateEvent, ThemeEvents, pendingCartMutations } from '@theme/events';

const SOURCE = 'fd-cart-sync';
const STORAGE_KEY = 'fd-cart-refresh';
let revision = 0;
let request = null;
let timer;
let cartState = null;
let refreshDeferred = false;

function configuration() {
  try {
    return JSON.parse(document.querySelector('[data-fd-shopping-config]')?.textContent || '{}');
  } catch {
    return {};
  }
}

function signature(cart) {
  return cart.items.map((item) => `${item.key}:${item.quantity}:${item.final_line_price}|`).join('') +
    (cart.cart_level_discount_applications || []).map((discount) => `${discount.title}:${discount.total_allocated_amount}|`).join('');
}

function money(amount, currency) {
  const config = configuration();
  return new Intl.NumberFormat(config.locale || document.documentElement.lang || undefined, {
    style: 'currency',
    currency: currency || config.currency || window.Shopify?.currency?.active,
    currencyDisplay: config.currencyCodeEnabled ? 'code' : 'narrowSymbol',
  }).format(amount / 100);
}

function showStatus(failed) {
  document.querySelectorAll('[data-fd-cart-sync-status]').forEach((element) => {
    element.textContent = failed ? element.dataset.syncErrorLabel || '' : '';
  });
}

function announceChange() {
  try {
    localStorage.setItem(STORAGE_KEY, `${Date.now()}:${Math.random()}`);
  } catch {}
}

function invalidate() {
  revision += 1;
  request?.abort();
  request = null;
  clearTimeout(timer);
}

function scheduleRefresh() {
  invalidate();
  refreshDeferred = pendingCartMutations.size > 0;
  if (refreshDeferred) return;
  timer = setTimeout(refresh, 90);
}

async function refresh() {
  if (pendingCartMutations.size) return;
  const currentRevision = revision;
  const controller = new AbortController();
  request = controller;
  const config = configuration();
  const route = config.routes?.cart || window.Theme?.routes?.cart_url;
  if (!route) return;
  const cartURL = new URL(route, location.origin);
  if (!cartURL.pathname.endsWith('.js')) cartURL.pathname += '.js';
  const sectionIds = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')]
    .map((element) => element.dataset.sectionId).filter(Boolean))].slice(0, 5);
  const sectionURL = new URL(location.href);
  sectionURL.searchParams.delete('section_id');
  sectionURL.searchParams.set('sections', sectionIds.join(','));
  try {
    const cartResponse = await fetch(cartURL, { credentials: 'same-origin', cache: 'no-store', signal: controller.signal });
    if (!cartResponse.ok) throw new Error();
    const cart = await cartResponse.json();
    if (!Array.isArray(cart.items) || !Number.isFinite(cart.total_price)) throw new Error();
    if (controller.signal.aborted || currentRevision !== revision || pendingCartMutations.size) return;
    let sections = {};
    if (sectionIds.length) {
      const sectionResponse = await fetch(sectionURL, { credentials: 'same-origin', cache: 'no-store', signal: controller.signal });
      if (!sectionResponse.ok) throw new Error();
      sections = await sectionResponse.json();
      if (sectionIds.some((id) => typeof sections[id] !== 'string')) throw new Error();
    }
    if (controller.signal.aborted || currentRevision !== revision) return;
    cartState = cart;
    document.dispatchEvent(new CartUpdateEvent(cart, SOURCE, {
      source: SOURCE,
      itemCount: cart.item_count,
      sections,
    }));
    document.querySelectorAll('fd-cart-estimate').forEach((element) => element.render(cart));
    showStatus(false);
  } catch (error) {
    if (!controller.signal.aborted && currentRevision === revision) showStatus(true);
  } finally {
    if (request === controller) request = null;
  }
}

class FdCartEstimate extends HTMLElement {
  connectedCallback() {
    if (cartState && Number(this.dataset.cartTotal) === cartState.total_price && this.dataset.cartSignature === signature(cartState)) this.render(cartState);
  }

  render(cart) {
    const taxValue = this.querySelector('[data-fd-cart-tax]');
    const totalValue = this.querySelector('[data-fd-cart-estimated-total]');
    const totalRow = this.querySelector('[data-fd-cart-estimated-row]');
    const note = this.querySelector('[data-fd-cart-estimate-note]');
    if (!taxValue) return;
    const included = typeof cart.taxes_included === 'boolean' ? cart.taxes_included : this.dataset.taxIncluded === 'true';
    const enabled = this.dataset.taxEnabled === 'true';
    const rate = Number(this.dataset.taxRate);
    let taxableSubtotal = null;
    const ssrMatches = Number(this.dataset.cartTotal) === cart.total_price && this.dataset.cartSignature === signature(cart);
    if (ssrMatches && Number.isFinite(Number(this.dataset.taxableSubtotal))) {
      taxableSubtotal = Number(this.dataset.taxableSubtotal);
    } else {
      const hasCartDiscounts = cart.cart_level_discount_applications?.length > 0;
      const taxableItems = cart.items.filter((item) => item.taxable);
      if (!taxableItems.length) taxableSubtotal = 0;
      else if (!hasCartDiscounts) taxableSubtotal = taxableItems.reduce((sum, item) => sum + item.final_line_price, 0);
      else if (taxableItems.length === cart.items.length) taxableSubtotal = cart.total_price;
    }
    const estimated = !included && enabled && Number.isFinite(rate) && rate >= 0 && taxableSubtotal !== null;
    totalRow && (totalRow.hidden = !estimated);
    note && (note.hidden = !estimated);
    if (!estimated) {
      taxValue.textContent = included ? this.dataset.taxIncludedLabel || '' : this.dataset.taxCheckoutLabel || '';
      return;
    }
    const tax = Math.round(Math.max(0, taxableSubtotal) * rate / 100);
    try {
      taxValue.textContent = money(tax, cart.currency);
      if (totalValue) totalValue.textContent = money(cart.total_price + tax, cart.currency);
    } catch {
      taxValue.textContent = this.dataset.taxCheckoutLabel || '';
      totalRow && (totalRow.hidden = true);
      note && (note.hidden = true);
    }
  }
}

if (!customElements.get('fd-cart-estimate')) customElements.define('fd-cart-estimate', FdCartEstimate);

function onNativeUpdate(event) {
  if (event.detail?.sourceId === SOURCE || event.detail?.data?.source === SOURCE) return;
  if (event.detail?.data?.didError) return;
  const resource = event.detail?.resource;
  cartState = Array.isArray(resource?.items) && Number.isFinite(resource?.total_price) ? resource : null;
  announceChange();
  scheduleRefresh();
}

document.addEventListener(ThemeEvents.cartUpdate, onNativeUpdate);
document.addEventListener(ThemeEvents.discountUpdate, onNativeUpdate);
document.addEventListener(ThemeEvents.cartMutationStart, invalidate);
document.addEventListener(ThemeEvents.cartMutationEnd, () => {
  if (refreshDeferred && !pendingCartMutations.size) scheduleRefresh();
});
document.addEventListener(ThemeEvents.quantitySelectorUpdate, invalidate, true);
document.addEventListener('submit', (event) => {
  if (event.target instanceof Element && event.target.closest('product-form-component, cart-discount-component')) invalidate();
}, true);
document.addEventListener('click', (event) => {
  if (event.target instanceof Element && event.target.closest('.cart-items__remove, .cart-discount__pill-remove')) invalidate();
}, true);
window.addEventListener('storage', (event) => {
  if (event.key === STORAGE_KEY) scheduleRefresh();
});
window.addEventListener('pageshow', scheduleRefresh);
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') scheduleRefresh();
});
window.addEventListener('pagehide', invalidate);
if (document.readyState === 'complete') scheduleRefresh();
